import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UsageMeter } from '@captionflow/shared';
import { api } from '../api/client';
import { Logo } from '../components/Logo';
import '../styles/billing.css';

function Meter({ label, m, unit }: { label: string; m: UsageMeter; unit?: string }) {
  const pct = m.limit > 0 ? Math.min(100, (m.used / m.limit) * 100) : 0;
  const full = m.remaining <= 0;
  return (
    <div className="bill-meter">
      <div className="bill-meter-top">
        <b>{label}</b>
        <span>
          {Math.round(m.used * 10) / 10} / {m.limit}
          {unit ? ` ${unit}` : ''}
        </span>
      </div>
      <div className="bill-bar">
        <i className={full ? 'full' : ''} style={{ width: `${Math.max(3, pct)}%` }} />
      </div>
    </div>
  );
}

function money(cents: number, currency: string): string {
  const amt = cents / 100;
  return currency === 'INR' ? `₹${amt.toLocaleString('en-IN')}` : `$${amt.toFixed(2)}`;
}

const STATUS_LABEL: Record<string, string> = {
  trialing: 'Free trial',
  trial_expired: 'Trial ended',
  active: 'Active',
  on_hold: 'Payment on hold',
  canceled: 'Canceled',
};

export function BillingPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const justCheckedOut = params.get('checkout') === 'success';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['billing-me'],
    queryFn: () => api.getBilling(),
    retry: false,
    // Poll briefly after returning from checkout so the webhook has time to land.
    refetchInterval: justCheckedOut ? 4000 : false,
  });

  const cancel = useMutation({
    mutationFn: () => api.cancelSubscription(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-me'] }),
  });
  const resume = useMutation({
    mutationFn: () => api.resumeSubscription(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-me'] }),
  });

  if (isError) {
    // Not signed in (or API down) → send to login.
    nav('/login');
    return null;
  }

  return (
    <div className="pay">
      <div className="pay-shell">
        <div className="pay-top">
          <Link to="/" aria-label="Kaption home"><Logo size={20} /></Link>
          <Link to="/" className="pay-back">← Dashboard</Link>
        </div>

        {isLoading || !data ? (
          <div className="bill-loading">Loading your plan…</div>
        ) : (
          <>
            <div className="pay-head" style={{ margin: '30px 0 4px' }}>
              <h1>Billing</h1>
            </div>

            {justCheckedOut && (
              <div className="bill-banner" style={{ background: '#eaf6f0', borderColor: '#c7e6d4', color: '#1f7a4d' }}>
                Payment received. Your plan will update within a minute.
              </div>
            )}

            {data.entitlements.status === 'on_hold' && (
              <div className="bill-banner">
                Your last renewal payment failed. Update your payment method to keep your plan active.
              </div>
            )}
            {data.entitlements.status === 'trial_expired' && (
              <div className="bill-banner">
                Your free trial has ended. Upgrade to keep creating.
              </div>
            )}

            {/* Current plan */}
            <div className="bill-section">
              <div className="bill-row">
                <div>
                  <div className="bill-plan-name">{data.entitlements.planName}</div>
                  <p className="bill-sub">
                    {data.entitlements.status === 'trialing' && data.entitlements.trialEndsAt
                      ? `Trial ends ${new Date(data.entitlements.trialEndsAt).toLocaleDateString()}`
                      : data.subscription?.currentPeriodEnd
                        ? `${data.subscription.cancelAtPeriodEnd ? 'Ends' : 'Renews'} ${new Date(
                            data.subscription.currentPeriodEnd,
                          ).toLocaleDateString()}`
                        : 'No card on file'}
                  </p>
                </div>
                <span className={`bill-status ${data.entitlements.status}`}>
                  {STATUS_LABEL[data.entitlements.status] ?? data.entitlements.status}
                </span>
              </div>

              <div className="bill-actions" style={{ marginTop: 16 }}>
                {data.entitlements.plan === 'pro' ? null : (
                  <Link to="/pricing" className="pay-btn fill">Upgrade plan</Link>
                )}
                {data.subscription && !data.subscription.cancelAtPeriodEnd && (
                  <button className="pay-btn" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                    {cancel.isPending ? 'Canceling…' : 'Cancel plan'}
                  </button>
                )}
                {data.subscription?.cancelAtPeriodEnd && (
                  <button className="pay-btn" onClick={() => resume.mutate()} disabled={resume.isPending}>
                    {resume.isPending ? 'Resuming…' : 'Resume plan'}
                  </button>
                )}
                {!data.subscription && data.entitlements.plan !== 'free' && (
                  <Link to="/pricing" className="pay-btn">Change plan</Link>
                )}
              </div>
            </div>

            {/* Usage this period */}
            <div className="bill-section">
              <h2>Usage this period</h2>
              <p className="bill-sub">
                {new Date(data.entitlements.periodStart).toLocaleDateString()} –{' '}
                {new Date(data.entitlements.periodEnd).toLocaleDateString()}
              </p>
              <div className="bill-meters" style={{ marginTop: 14 }}>
                <Meter label="Video minutes" m={data.entitlements.usage.minutes} unit="min" />
                <Meter label="kMagic runs" m={data.entitlements.usage.kmagic} />
                <Meter label="Depth exports" m={data.entitlements.usage.depth} />
              </div>
            </div>

            {/* GST / India */}
            {data.entitlements.currency === 'INR' && (
              <div className="bill-section">
                <h2>GST</h2>
                <p className="bill-sub">
                  You’re billed in ₹ with 18% GST. A GST invoice is issued for every payment.
                  {data.gstin ? ` GSTIN on file: ${data.gstin}.` : ' Add a GSTIN at checkout for a business invoice.'}
                </p>
              </div>
            )}

            {/* Invoices */}
            <div className="bill-section">
              <h2>Payment history</h2>
              {data.invoices.length === 0 ? (
                <p className="bill-empty" style={{ marginTop: 10 }}>No payments yet.</p>
              ) : (
                <table className="bill-invoices" style={{ marginTop: 10 }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                        <td>{money(inv.amountCents, inv.currency)}</td>
                        <td className="cap">{inv.status}</td>
                        <td>
                          {inv.invoiceUrl ? (
                            <a href={inv.invoiceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--deep)' }}>
                              Invoice ↗
                            </a>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <p className="pay-note">
              Payments and tax are handled by Dodo Payments (merchant of record). Manage or update
              your card from any invoice.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
